import api from "./client";

export const scenariosAPI = {
  getScenarios: async (clientId) => {
    const response = await api.get("/api/v1/scenarios", {
      params: { client_id: clientId },
    });
    return response;
  },
  getScenariosByAgent: async (agentId, clientId) => {
    const response = await api.get(`/api/v1/agents/${agentId}/scenarios`, {
      params: { client_id: clientId },
    });
    return response.data;
  },
  getScenariosWithPagination: async (page = 1, clientId, agentId = null, opts = {}) => {
    const params = { client_id: clientId };
    if (agentId) params.agent_id = agentId;
    if (opts.includeArchived) params.include_archived = true;
    if (opts.archivedOnly) params.archived_only = true;
    if (opts.search?.trim()) params.search = opts.search.trim();
    const response = await api.get(`/api/v1/scenarios?page=${page}`, { params });
    return response;
  },
  getScenario: async (id, clientId) => {
    const response = await api.get(`/api/v1/scenarios/${id}`, {
      params: { client_id: clientId },
    });
    return response;
  },
  // Metrics applicable to an agent (for the per-agent scenario picker), each
  // flagged whether it's a simulation default.
  getAgentMetricOptions: async (agentId, clientId) => {
    const response = await api.get("/api/v1/scenarios/agent_metric_options", {
      params: { client_id: clientId, agent_id: agentId },
    });
    return response.data;
  },
  createScenario: async (data, clientId) => {
    const response = await api.post("/api/v1/scenarios", data, {
      params: { client_id: clientId },
    });
    return response;
  },
  updateScenario: async (id, data, clientId) => {
    const response = await api.put(`/api/v1/scenarios/${id}`, data, {
      params: { client_id: clientId },
    });
    return response;
  },
  deleteScenario: async (id, clientId) => {
    const response = await api.delete(`/api/v1/scenarios/${id}`, {
      params: { client_id: clientId },
    });
    return response;
  },
  archiveScenario: async (id, clientId) => {
    const response = await api.post(`/api/v1/scenarios/${id}/archive`, null, {
      params: { client_id: clientId },
    });
    return response;
  },
  unarchiveScenario: async (id, clientId) => {
    const response = await api.post(`/api/v1/scenarios/${id}/unarchive`, null, {
      params: { client_id: clientId },
    });
    return response;
  },
  generateScenarios: async (data, clientId) => {
    const response = await api.post("/api/v1/scenarios/generate", data, {
      params: { client_id: clientId },
    });
    return response;
  },
  generateFromReplay: async (callObservationId, agentId, clientId) => {
    const response = await api.post("/api/v1/scenarios/generate_from_replay", {
      call_observation_id: callObservationId,
      agent_id: agentId,
    }, {
      params: { client_id: clientId },
    });
    return response.data;
  },
  // Batch methods (kept on scenariosAPI for backward compatibility)
  getBatches: async (clientId) => {
    const response = await api.get("/api/v1/batches", {
      params: { client_id: clientId },
    });
    if (response && response.data) {
      return response;
    } else {
      console.warn("Unexpected API response format:", response);
      return { data: [] };
    }
  },
  getBatchesWithPagination: async (page = 1, clientId, perPage = 10, agentId = null, opts = {}) => {
    const params = { client_id: clientId, per_page: perPage };
    if (agentId) params.agent_id = agentId;
    if (opts.search?.trim()) params.search = opts.search.trim();
    if (opts.status && opts.status !== "all") params.status = opts.status;
    const response = await api.get(`/api/v1/batches?page=${page}`, {
      params,
    });
    if (response && response.data) {
      return response;
    } else {
      console.warn("Unexpected API response format:", response);
      return { data: [] };
    }
  },
  createBatch: async (data, clientId) => {
    const response = await api.post("/api/v1/batches", data, {
      params: { client_id: clientId },
    });
    return response;
  },
  getBatch: async (id, clientId) => {
    const response = await api.get(`/api/v1/batches/${id}`, {
      params: { client_id: clientId },
    });
    return response;
  },
  getBatchRuns: async (batchId, clientId) => {
    const response = await api.get(`/api/v1/batches/${batchId}/runs`, {
      params: { client_id: clientId },
    });
    return response;
  },
  getRun: async (batchId, runId) => {
    const response = await api.get(`/api/v1/batches/${batchId}/runs/${runId}`);
    return response;
  },
  stopRun: async (batchId, runId) => {
    const response = await api.post(`/api/v1/batches/${batchId}/runs/${runId}/stop`);
    return response;
  },
  rerunRun: async (batchId, runId) => {
    const response = await api.post(`/api/v1/batches/${batchId}/runs/${runId}/rerun`);
    return response;
  },
  rerunMetrics: async (batchId, runId, metricIds = null) => {
    const body = metricIds && metricIds.length ? { metric_ids: metricIds } : {};
    const response = await api.post(`/api/v1/batches/${batchId}/runs/${runId}/rerun_metrics`, body);
    return response;
  },
  getLiveToken: async (batchId, runId) => {
    const response = await api.get(`/api/v1/batches/${batchId}/runs/${runId}/live_token`);
    return response;
  },
  rerunBatch: async (batchId) => {
    const response = await api.post(`/api/v1/batches/${batchId}/rerun`);
    return response;
  },
  getRunCalls: async (batchId, runId) => {
    const response = await api.get(
      `/api/v1/batches/${batchId}/runs/${runId}/calls`
    );
    return response;
  },
  addRunTag: async (batchId, runId, tag) => {
    const response = await api.post(
      `/api/v1/batches/${batchId}/runs/${runId}/add_tag`,
      {
        tag: tag,
      }
    );
    return response;
  },
  removeRunTag: async (batchId, runId, tag) => {
    const response = await api.delete(
      `/api/v1/batches/${batchId}/runs/${runId}/remove_tag`,
      {
        data: { tag: tag },
      }
    );
    return response;
  },
};

export const metadataAPI = {
  getMetadata: async (clientId) => {
    const response = await api.get("/api/v1/metadata", {
      params: { client_id: clientId },
    });
    return response;
  },
  createMetadata: async (data, clientId) => {
    const response = await api.post("/api/v1/metadata", {
      metadata: { ...data, client_id: clientId },
    });
    return response;
  },
};

export const metadataGroupsAPI = {
  getGroups: async (clientId, agentId) => {
    const params = { client_id: clientId };
    if (agentId) params.agent_id = agentId;
    const response = await api.get("/api/v1/metadata_groups", { params });
    return response.data;
  },
  createGroup: async (data, clientId) => {
    const response = await api.post("/api/v1/metadata_groups", {
      client_id: clientId,
      metadata_group: data,
    });
    return response.data;
  },
  updateGroup: async (id, data, clientId) => {
    const response = await api.put(`/api/v1/metadata_groups/${id}`, {
      client_id: clientId,
      metadata_group: data,
    });
    return response.data;
  },
  deleteGroup: async (id, clientId) => {
    await api.delete(`/api/v1/metadata_groups/${id}`, { params: { client_id: clientId } });
  },
};

export const contentImproverAPI = {
  improveContent: async (content, improvementType) => {
    const response = await api.post("/api/v1/improve-content", {
      content,
      improvement_type: improvementType,
    });
    return response.data;
  },
};
